import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Shirt,
  PoundSterling,
  Shield,
  ArrowLeftRight,
  XCircle,
  CalendarClock,
} from 'lucide-react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isAfter,
  parseISO,
} from 'date-fns';
import { cn } from '../../lib/utils';
import { formatUK } from '../../lib/ukTime';
import { toast } from 'sonner';
import { FileText as JobDocIcon, Download as JobDocDownload } from 'lucide-react';

function JobDocumentsCard() {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/me/job-documents`).then((res) => setDocs(res.data || [])).catch(() => {});
  }, []);
  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/me/job-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };
  if (!docs.length) return null;
  return (
    <Card className="bg-card border-border rounded-sm" data-testid="job-documents-card">
      <CardHeader className="border-b border-border pb-3 pt-4 px-4">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <JobDocIcon className="w-4 h-4 text-primary" />
          Job Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 divide-y divide-border">
        {docs.map((d) => (
          <div key={d.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`job-doc-${d.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground truncate">{d.file_name}</p>
              {d.request_site && <p className="text-xs text-muted-foreground truncate">{d.request_site}</p>}
            </div>
            <Button size="icon" variant="ghost" onClick={() => download(d)} className="h-8 w-8 text-muted-foreground hover:text-primary shrink-0" data-testid={`job-doc-download-${d.id}`}>
              <JobDocDownload className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pretty "Xh Ym" — empty when invalid.
const minutesBetween = (a, b) => {
  if (!a || !b) return 0;
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  if ([ah, am, bh, bm].some(Number.isNaN)) return 0;
  let diff = bh * 60 + bm - (ah * 60 + am);
  if (diff < 0) diff += 24 * 60; // crosses midnight
  return diff;
};
const fmtHours = (mins) => {
  if (!mins) return '0 hours';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${h}h ${m}m`;
};

// Status taxonomy for the calendar legend + day colouring.
const dayBucket = (dayShifts, todayStr, dayStr) => {
  if (!dayShifts.length) return 'none';
  const allCompleted = dayShifts.every((s) => s.status === 'completed');
  const anyInProgress = dayShifts.some((s) => s.status === 'in_progress');
  if (allCompleted) return 'completed';
  if (dayStr === todayStr || anyInProgress) return 'active';
  return 'upcoming';
};

export default function EmployeeShiftsPage() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const navigate = useNavigate();

  const fetchShifts = useCallback(async () => {
    const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    try {
      const response = await axios.get(`${API}/shifts/calendar`, { params: { start_date: start, end_date: end } });
      setShifts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  // Employees always see the latest authorised schedule automatically.
  useAutoRefresh(fetchShifts);

  const getShiftsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter((s) => s.date === dateStr);
  };

  const days = useMemo(() => eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  }), [currentMonth]);

  const selectedDateShifts = selectedDate ? getShiftsForDate(selectedDate) : [];
  const todayStr = formatUK(new Date().toISOString(), 'yyyy-MM-dd');

  // Total scheduled minutes for the visible month — derived once so the
  // "X hours scheduled" line stays cheap regardless of how many shifts.
  const monthStats = useMemo(() => {
    const total = shifts.reduce(
      (acc, s) => acc + minutesBetween(s.start_time, s.end_time),
      0,
    );
    return { count: shifts.length, minutes: total };
  }, [shifts]);

  // Upcoming shifts (today or later, not completed) sorted ascending.
  const upcoming = useMemo(() => {
    return shifts
      .filter((s) => s.date && s.date >= todayStr && s.status !== 'completed')
      .sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.start_time || '').localeCompare(b.start_time || '');
      });
  }, [shifts, todayStr]);

  const requestSwap = () => {}; // removed 2026-02 — module retired

  if (loading) {
    return (
      <EmployeeLayout>
        <div className="py-8 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <div className="py-6 px-3 sm:px-4 space-y-6 max-w-3xl mx-auto">
        <div className="text-center">
          <h1 className="text-xl font-barlow tracking-widest flex items-center justify-center gap-2">
            <CalendarIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
            MY SHIFTS
          </h1>
        </div>

        {/* Documents shared by admin for jobs I'm assigned to */}
        <JobDocumentsCard />

        {/* Calendar */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border pb-3 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-barlow tracking-wider uppercase">
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="h-8 w-8" data-testid="prev-month-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="h-8 w-8" data-testid="next-month-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} className="text-center text-xs text-muted-foreground py-1.5 font-medium">{day}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: days[0].getDay() }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}

              {days.map((day) => {
                const dayShifts = getShiftsForDate(day);
                const dayStr = format(day, 'yyyy-MM-dd');
                const bucket = dayBucket(dayShifts, todayStr, dayStr);
                const isToday = dayStr === todayStr;
                const isSelected = selectedDate && isSameDay(day, selectedDate);

                // Colour mapping per spec
                let cls = 'bg-muted/40 text-foreground'; // no shift = neutral
                if (bucket === 'completed') cls = 'bg-red-500 text-white';
                else if (bucket === 'active') cls = 'bg-yellow-500 text-black';
                else if (bucket === 'upcoming') cls = 'bg-green-600 text-white';

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    data-testid={`calendar-day-${dayStr}`}
                    className={cn(
                      'aspect-square flex flex-col items-center justify-center rounded-sm transition-all text-xs font-medium gap-0.5 p-0.5',
                      cls,
                      isToday && 'ring-2 ring-yellow-400 ring-offset-1 ring-offset-card',
                      isSelected && 'outline outline-2 outline-primary',
                    )}
                  >
                    <span className="text-sm leading-none">{format(day, 'd')}</span>
                    {dayShifts.length > 0 && (
                      <span
                        className="text-[9px] leading-none px-1 py-0.5 rounded-sm bg-black/30 text-white whitespace-nowrap"
                        data-testid={`day-badge-${dayStr}`}
                      >
                        {dayShifts.length === 1 && dayShifts[0].start_time
                          ? dayShifts[0].start_time
                          : `${dayShifts.length} shift${dayShifts.length === 1 ? '' : 's'}`}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]" data-testid="calendar-legend">
              <LegendDot color="bg-green-600" label="Upcoming shift" />
              <LegendDot color="bg-yellow-500" label="Active today" />
              <LegendDot color="bg-red-500" label="Completed shift" />
              <LegendDot color="bg-muted/40" label="No shift" border />
            </div>
          </CardContent>
        </Card>

        {/* Month stats */}
        <div className="text-center space-y-0.5">
          <p className="text-sm text-muted-foreground">
            {monthStats.count} shift{monthStats.count === 1 ? '' : 's'} this month
          </p>
          <p className="text-xs text-muted-foreground/80">
            {fmtHours(monthStats.minutes)} scheduled
          </p>
        </div>

        {/* Detailed view for selected day */}
        {selectedDate && (
          <Card className="bg-card border-border rounded-sm" data-testid="selected-day-detail">
            <CardHeader className="border-b border-border pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-barlow tracking-wider uppercase text-primary">
                {format(selectedDate, 'EEEE, MMM d yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {selectedDateShifts.length > 0 ? (
                <div className="divide-y divide-border">
                  {selectedDateShifts.map((shift) => (
                    <ShiftDetail
                      key={shift.id}
                      shift={shift}
                      onSwap={requestSwap}
                      todayStr={todayStr}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  <p className="text-sm">No shifts scheduled</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Upcoming shifts list */}
        {upcoming.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" />
              Upcoming Shifts
            </p>
            <div className="space-y-2" data-testid="upcoming-shifts-list">
              {upcoming.map((s) => (
                <button
                  key={`up-${s.id}`}
                  onClick={() => setSelectedDate(parseISO(s.date))}
                  className="w-full text-left bg-card border border-border rounded-sm p-3 hover:border-primary/50 transition-colors"
                  data-testid={`upcoming-${s.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">
                      {format(parseISO(s.date), 'EEE, dd MMM')}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground">
                      {s.start_time && s.end_time ? `${s.start_time} – ${s.end_time}` : 'Times pending'}
                    </p>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
                    {s.site_name && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {s.site_name}
                      </span>
                    )}
                    {s.position && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {s.position}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}

function LegendDot({ color, label, border }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn('w-3 h-3 rounded-sm', color, border && 'border border-border')} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

// -------- Single shift detail block --------
function ShiftDetail({ shift, onSwap, todayStr }) {
  // Derive a friendly status for the spec: Upcoming / Active / Completed.
  const niceStatus = (() => {
    if (shift.status === 'completed') return { label: 'Completed', cls: 'bg-red-500/10 text-red-400 border-red-500/30' };
    if (shift.status === 'in_progress' || shift.date === todayStr) return { label: 'Active', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' };
    return { label: 'Upcoming', cls: 'bg-green-500/10 text-green-400 border-green-500/30' };
  })();
  const isFuture = shift.date && shift.date > todayStr;
  const canSwap = isFuture && shift.status !== 'completed';

  return (
    <div className="p-4 space-y-3" data-testid={`shift-${shift.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          {/* Event name (role) — bumped to a prominent label since the
              spec asks for it at the top of the detail view. Reads from
              `shift_role` (set by admin Edit Shift dialog) and falls back
              to `position` for older shift documents. */}
          {(shift.shift_role || shift.position) && (
            <p className="text-base font-semibold text-foreground">{shift.shift_role || shift.position}</p>
          )}
          {(shift.start_time || shift.end_time) ? (
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
              <span className="font-mono text-sm text-muted-foreground">
                {shift.start_time && shift.end_time
                  ? `${shift.start_time} - ${shift.end_time}`
                  : (shift.start_time || shift.end_time || 'Times Pending')}
              </span>
            </div>
          ) : (
            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px] uppercase">
              Times pending
            </Badge>
          )}
        </div>
        <Badge className={cn('text-[10px] uppercase tracking-wider border', niceStatus.cls)}>
          {niceStatus.label}
        </Badge>
      </div>

      {shift.site_name && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          <span className="text-sm">{shift.site_name}{shift.postcode && ` (${shift.postcode})`}</span>
        </div>
      )}

      {shift.dress_code && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Shirt className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          <span className="text-sm">{shift.dress_code}</span>
        </div>
      )}

      {shift.hourly_payment && (
        <div className="flex items-center gap-2 text-green-400">
          <PoundSterling className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          <span className="text-sm font-medium">£{Number(shift.hourly_payment).toFixed(2)} / hour</span>
        </div>
      )}

      {shift.sia_required && shift.sia_required !== 'none' && (
        <div className="flex items-center gap-2 text-yellow-400">
          <Shield className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          <span className="text-sm">
            SIA: {shift.sia_required === 'door_supervisor'
              ? 'Door Supervisor'
              : shift.sia_required === 'cctv' ? 'CCTV' : shift.sia_required}
          </span>
        </div>
      )}

      {canSwap && (
        <div className="mt-2 rounded-sm bg-emerald-500/5 border border-emerald-500/30 px-3 py-2 text-[11px] text-emerald-100 leading-relaxed">
          Need to swap or drop this shift? Please contact Admin via WhatsApp on <a href="https://wa.me/447365928174" target="_blank" rel="noopener noreferrer" className="underline">+44 7365 928174</a>.
        </div>
      )}
    </div>
  );
}
